import { Component, ChangeDetectionStrategy } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-enterprise';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { Bill } from './bill-data.service';

@Component({
  selector: 'app-billing-actions-cell-renderer',
  standalone: true,
  imports: [ButtonModule, TooltipModule, FontAwesomeModule],
  template: `
    <div class="action-buttons-container">
      @if (showDownload()) {
        <p-button
          [text]="true"
          size="small"
          severity="secondary"
          pTooltip="Download PDF"
          tooltipPosition="top"
          (onClick)="onDownload()">
          <fa-icon [icon]="['fas', 'download']" size="sm"></fa-icon>
        </p-button>
      }
      @if (showIssue()) {
        <p-button
          [text]="true"
          size="small"
          severity="secondary"
          pTooltip="Issue invoice"
          tooltipPosition="top"
          (onClick)="onIssue()">
          <fa-icon [icon]="['fas', 'check']" size="sm"></fa-icon>
        </p-button>
      }
      @if (showSendEmail()) {
        <p-button
          [text]="true"
          size="small"
          severity="info"
          pTooltip="Send email"
          tooltipPosition="top"
          (onClick)="onSendEmail()">
          <fa-icon [icon]="['fas', 'envelope']" size="sm"></fa-icon>
        </p-button>
      }
      @if (showDiscard()) {
        <p-button
          [text]="true"
          size="small"
          severity="danger"
          pTooltip="Discard draft"
          tooltipPosition="top"
          (onClick)="onDiscard()">
          <fa-icon [icon]="['fas', 'trash']" size="sm"></fa-icon>
        </p-button>
      }
      @if (showMarkPaid()) {
        <p-button
          [text]="true"
          size="small"
          severity="success"
          pTooltip="Mark as paid"
          tooltipPosition="top"
          (onClick)="onMarkPaid()">
          <fa-icon [icon]="['fas', 'check']" size="sm"></fa-icon>
        </p-button>
      }
      @if (showCancel()) {
        <p-button
          [text]="true"
          size="small"
          severity="danger"
          pTooltip="Cancel bill"
          tooltipPosition="top"
          (onClick)="onCancel()">
          <fa-icon [icon]="['fas', 'ban']" size="sm"></fa-icon>
        </p-button>
      }
      @if (showNewVersion()) {
        <p-button
          [text]="true"
          size="small"
          severity="secondary"
          pTooltip="Create new version"
          tooltipPosition="top"
          (onClick)="onNewVersion()">
          <fa-icon [icon]="['fas', 'copy']" size="sm"></fa-icon>
        </p-button>
      }
      @if (showArchive()) {
        <p-button
          [text]="true"
          size="small"
          severity="secondary"
          pTooltip="Archive bill"
          tooltipPosition="top"
          (onClick)="onArchive()">
          <fa-icon [icon]="['fas', 'box-archive']" size="sm"></fa-icon>
        </p-button>
      }
      @if (showRestore()) {
        <p-button
          [text]="true"
          size="small"
          severity="secondary"
          pTooltip="Restore bill"
          tooltipPosition="top"
          (onClick)="onRestore()">
          <fa-icon [icon]="['fas', 'rotate-left']" size="sm"></fa-icon>
        </p-button>
      }
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .action-buttons-container {
        display: flex;
        align-items: center;
        gap: 0.125rem;
        padding-left: 0;
        margin-left: 0;
      }
    `,
  ],
})
export class BillingActionsCellRendererComponent implements ICellRendererAngularComp {
  data: Bill | undefined;

  downloadFn: ((bill: Bill) => void) | undefined;
  issueFn: ((bill: Bill) => void) | undefined;
  sendEmailFn: ((bill: Bill) => void) | undefined;
  discardFn: ((bill: Bill) => void) | undefined;
  markPaidFn: ((bill: Bill) => void) | undefined;
  cancelFn: ((bill: Bill) => void) | undefined;
  newVersionFn: ((bill: Bill) => void) | undefined;
  archiveFn: ((bill: Bill) => void) | undefined;
  restoreFn: ((bill: Bill) => void) | undefined;

  downloadCondition: ((bill: Bill) => boolean) | undefined;
  issueCondition: ((bill: Bill) => boolean) | undefined;
  sendEmailCondition: ((bill: Bill) => boolean) | undefined;
  discardCondition: ((bill: Bill) => boolean) | undefined;
  markPaidCondition: ((bill: Bill) => boolean) | undefined;
  cancelCondition: ((bill: Bill) => boolean) | undefined;
  newVersionCondition: ((bill: Bill) => boolean) | undefined;
  archiveCondition: ((bill: Bill) => boolean) | undefined;
  restoreCondition: ((bill: Bill) => boolean) | undefined;

  agInit(params: ICellRendererParams): void {
    this.data = params.data;

    this.downloadFn = params.context['downloadFn'];
    this.issueFn = params.context['issueFn'];
    this.sendEmailFn = params.context['sendEmailFn'];
    this.discardFn = params.context['discardFn'];
    this.markPaidFn = params.context['markPaidFn'];
    this.cancelFn = params.context['cancelFn'];
    this.newVersionFn = params.context['newVersionFn'];
    this.archiveFn = params.context['archiveFn'];
    this.restoreFn = params.context['restoreFn'];

    this.downloadCondition = params.context['downloadCondition'];
    this.issueCondition = params.context['issueCondition'];
    this.sendEmailCondition = params.context['sendEmailCondition'];
    this.discardCondition = params.context['discardCondition'];
    this.markPaidCondition = params.context['markPaidCondition'];
    this.cancelCondition = params.context['cancelCondition'];
    this.newVersionCondition = params.context['newVersionCondition'];
    this.archiveCondition = params.context['archiveCondition'];
    this.restoreCondition = params.context['restoreCondition'];
  }

  refresh(_params: ICellRendererParams): boolean {
    return false;
  }

  showDownload(): boolean {
    if (!this.downloadFn || !this.data) return false;
    return this.downloadCondition ? this.downloadCondition(this.data) : true;
  }

  showIssue(): boolean {
    if (!this.issueFn || !this.data) return false;
    return this.issueCondition ? this.issueCondition(this.data) : true;
  }

  showSendEmail(): boolean {
    if (!this.sendEmailFn || !this.data) return false;
    return this.sendEmailCondition ? this.sendEmailCondition(this.data) : true;
  }

  showDiscard(): boolean {
    if (!this.discardFn || !this.data) return false;
    return this.discardCondition ? this.discardCondition(this.data) : true;
  }

  showMarkPaid(): boolean {
    if (!this.markPaidFn || !this.data) return false;
    return this.markPaidCondition ? this.markPaidCondition(this.data) : true;
  }

  showCancel(): boolean {
    if (!this.cancelFn || !this.data) return false;
    return this.cancelCondition ? this.cancelCondition(this.data) : true;
  }

  showNewVersion(): boolean {
    if (!this.newVersionFn || !this.data) return false;
    return this.newVersionCondition ? this.newVersionCondition(this.data) : true;
  }

  showArchive(): boolean {
    if (!this.archiveFn || !this.data) return false;
    return this.archiveCondition ? this.archiveCondition(this.data) : true;
  }

  showRestore(): boolean {
    if (!this.restoreFn || !this.data) return false;
    return this.restoreCondition ? this.restoreCondition(this.data) : false;
  }

  onDownload(): void {
    if (this.downloadFn && this.data) this.downloadFn(this.data);
  }

  onIssue(): void {
    if (this.issueFn && this.data) this.issueFn(this.data);
  }

  onSendEmail(): void {
    if (this.sendEmailFn && this.data) this.sendEmailFn(this.data);
  }

  onDiscard(): void {
    if (this.discardFn && this.data) this.discardFn(this.data);
  }

  onMarkPaid(): void {
    if (this.markPaidFn && this.data) this.markPaidFn(this.data);
  }

  onCancel(): void {
    if (this.cancelFn && this.data) this.cancelFn(this.data);
  }

  onNewVersion(): void {
    if (this.newVersionFn && this.data) this.newVersionFn(this.data);
  }

  onArchive(): void {
    if (this.archiveFn && this.data) this.archiveFn(this.data);
  }

  onRestore(): void {
    if (this.restoreFn && this.data) this.restoreFn(this.data);
  }
}
